<template>
  <div :class="['disclaimer', colorClass]">
    <div class="disclaimer-tag">Disclaimer</div>
    <div class="disclaimer-header">
      <div class="disclaimer-icon">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 5v3M8 11h.01M14 8A6 6 0 112 8a6 6 0 0112 0z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
      <span class="disclaimer-title">{{ cfg.title || 'Informação importante' }}</span>
    </div>
    <div class="disclaimer-body" v-if="cfg.body" v-html="bodyHtml"></div>

    <div class="disclaimer-steps" v-if="cfg.show_steps && stepsHtml.length">
      <div
        v-for="(step, idx) in stepsHtml"
        :key="idx"
        class="disclaimer-step"
      >
        <div class="step-number">{{ idx + 1 }}</div>
        <div class="step-content">
          <div class="step-text" v-html="step.html"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'IDisclaimer',
  props: {
    config: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const cfg = computed(() => props.config || {});
    const colorClass = computed(() => {
      const c = cfg.value.color;
      return ['info', 'danger', 'purple', 'neutral'].includes(c) ? c : '';
    });
    const steps = computed(() => {
      const s = cfg.value.steps;
      return Array.isArray(s) ? s : [];
    });

    const externalLinks = (html) => {
      if (!html) return '';
      return html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
    };

    const bodyHtml = computed(() => externalLinks(cfg.value.body));
    const stepsHtml = computed(() => steps.value.map(s => ({ ...s, html: externalLinks(s.text) })));

    return { cfg, colorClass, steps, bodyHtml, stepsHtml };
  }
};
</script>

<style scoped>
.disclaimer {
  --amber: #e8a317;
  --amber-bg: #fefaf0;
  --amber-border: #f5e6b8;
  --amber-text: #7a5d0e;
  --amber-text-2: #9a7a2e;
  --primary: #4f6ef7;
  --primary-light: #eef1fe;

  background: var(--amber-bg);
  border: 1px solid var(--amber-border);
  border-radius: 0.375rem; /* rounded-md */
  padding: 22px 24px;
  width: 100%;
}

/* Tag */
.disclaimer-tag {
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  margin-bottom: 6px;
  padding-left: 38px;
  color: var(--amber);
}

.disclaimer.info .disclaimer-tag {
  color: var(--primary);
}

/* Header */
.disclaimer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.disclaimer-icon {
  width: 28px;
  height: 28px;
  background: rgba(232, 163, 23, 0.12);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--amber);
}

.disclaimer.info .disclaimer-icon {
  background: rgba(79, 110, 247, 0.1);
  color: var(--primary);
}

.disclaimer-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--amber-text);
}

.disclaimer.info .disclaimer-title {
  color: #2d3a8c;
}

/* Body */
.disclaimer-body {
  font-size: 0.8125rem;
  line-height: 1.7;
  color: var(--amber-text-2);
  padding-left: 38px;
}

.disclaimer.info .disclaimer-body {
  color: #4a5694;
}

.disclaimer-body :deep(a),
.disclaimer-body :deep(a:visited),
.disclaimer-body :deep(a:active),
.disclaimer-body :deep(a:focus) {
  color: inherit !important;
  font-weight: 600;
  text-decoration: none !important;
  border-bottom: 1px dashed currentColor;
  transition: opacity 0.2s;
}

.disclaimer-body :deep(a:hover) {
  opacity: 0.75;
}

/* Steps */
.disclaimer-steps {
  margin-top: 16px;
  padding-left: 38px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.disclaimer-step {
  display: flex;
  gap: 14px;
  position: relative;
  padding-bottom: 16px;
}

.disclaimer-step:last-child {
  padding-bottom: 0;
}

.disclaimer-step:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 13px;
  top: 28px;
  bottom: 0;
  width: 2px;
  background: var(--amber-border);
}

.disclaimer.info .disclaimer-step:not(:last-child)::before {
  background: #d4dcfb;
}

.step-number {
  width: 28px;
  height: 28px;
  background: #fff;
  border: 2px solid var(--amber-border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--amber-text);
  position: relative;
  z-index: 1;
}

.disclaimer.info .step-number {
  border-color: #c4cef8;
  color: #2d3a8c;
}

.step-content {
  flex: 1;
  padding-top: 4px;
}

.step-text {
  font-size: 0.8125rem;
  line-height: 1.65;
  color: var(--amber-text-2);
}

.disclaimer.info .step-text {
  color: #4a5694;
}

.step-text :deep(a),
.step-text :deep(a:visited),
.step-text :deep(a:active),
.step-text :deep(a:focus) {
  color: inherit !important;
  font-weight: 600;
  text-decoration: none !important;
  border-bottom: 1px dashed currentColor;
  transition: opacity 0.2s;
}

.step-text :deep(a:hover) {
  opacity: 0.75;
}

.step-text :deep(strong) {
  color: var(--amber-text);
  font-weight: 700;
}

.disclaimer.info .step-text :deep(strong) {
  color: #2d3a8c;
}

/* Info variant */
.disclaimer.info {
  background: var(--primary-light);
  border-color: #d4dcfb;
}

/* Danger variant */
.disclaimer.danger {
  background: #fef2f2;
  border-color: #fecaca;
}
.disclaimer.danger .disclaimer-tag { color: #ef4444; }
.disclaimer.danger .disclaimer-icon { background: rgba(239, 68, 68, 0.1); color: #ef4444; }
.disclaimer.danger .disclaimer-title { color: #991b1b; }
.disclaimer.danger .disclaimer-body { color: #b91c1c; }
.disclaimer.danger .step-text { color: #b91c1c; }
.disclaimer.danger .step-text :deep(strong) { color: #991b1b; }
.disclaimer.danger .step-number { border-color: #fecaca; color: #991b1b; }
.disclaimer.danger .disclaimer-step:not(:last-child)::before { background: #fecaca; }

/* Purple variant */
.disclaimer.purple {
  background: #f5f3ff;
  border-color: #ddd6fe;
}
.disclaimer.purple .disclaimer-tag { color: #8b5cf6; }
.disclaimer.purple .disclaimer-icon { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
.disclaimer.purple .disclaimer-title { color: #5b21b6; }
.disclaimer.purple .disclaimer-body { color: #6d28d9; }
.disclaimer.purple .step-text { color: #6d28d9; }
.disclaimer.purple .step-text :deep(strong) { color: #5b21b6; }
.disclaimer.purple .step-number { border-color: #ddd6fe; color: #5b21b6; }
.disclaimer.purple .disclaimer-step:not(:last-child)::before { background: #ddd6fe; }

/* Neutral variant */
.disclaimer.neutral {
  background: #f9fafb;
  border-color: #e5e7eb;
}
.disclaimer.neutral .disclaimer-tag { color: #6b7280; }
.disclaimer.neutral .disclaimer-icon { background: rgba(107, 114, 128, 0.1); color: #6b7280; }
.disclaimer.neutral .disclaimer-title { color: #374151; }
.disclaimer.neutral .disclaimer-body { color: #4b5563; }
.disclaimer.neutral .step-text { color: #4b5563; }
.disclaimer.neutral .step-text :deep(strong) { color: #374151; }
.disclaimer.neutral .step-number { border-color: #e5e7eb; color: #374151; }
.disclaimer.neutral .disclaimer-step:not(:last-child)::before { background: #e5e7eb; }

/* Responsive */
@media (max-width: 480px) {
  .disclaimer { padding: 18px 16px; }
  .disclaimer-body,
  .disclaimer-steps { padding-left: 0; }
  .disclaimer-tag { padding-left: 0; }
  .disclaimer-header { margin-bottom: 12px; }
}
</style>
